import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from './ui/card';
import { Trophy, Sparkles, CheckCircle2, Clock, FlaskConical } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Weekly Quest card — shows the active personalised challenge with live
 * progress bar. Re-fetches on language change and once per mount.
 * Also fetches Lab-driven quests (separate endpoint) and renders them with
 * a distinct purple/flask styling so the user understands the difference:
 * regular = "weekly habit goal", lab quest = "follow-up to your bloodwork".
 */
export function WeeklyQuestCard() {
    const { language } = useLanguage();
    const [quest, setQuest] = useState(null);
    const [labQuests, setLabQuests] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [regRes, labRes] = await Promise.all([
                    axios.get(`${API}/quests/current?language=${language}`),
                    axios.get(`${API}/quests/lab`).catch(() => ({ data: [] })),
                ]);
                if (cancelled) return;
                const data = regRes.data;
                if (data && !data.empty) {
                    setQuest(data);
                    if (data.just_completed) {
                        toast.success(
                            language === 'de'
                                ? `🏆 Quest abgeschlossen! +${data.reward_xp} XP`
                                : `🏆 Quest complete! +${data.reward_xp} XP`,
                            { duration: 5000 }
                        );
                    }
                }
                setLabQuests(Array.isArray(labRes.data) ? labRes.data : []);
            } catch (_e) { /* silent */ }
            finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [language]);

    if (loading) return null;
    if (!quest && labQuests.length === 0) return null;

    return (
        <div className="space-y-3">
            {quest && <RegularQuestRow quest={quest} language={language} />}
            {labQuests.map((q) => (
                <LabQuestRow key={q.id} quest={q} language={language} />
            ))}
        </div>
    );
}


function RegularQuestRow({ quest, language }) {
    const isDone = quest.status === 'completed';
    const isExpired = quest.status === 'expired';
    const pct = quest.progress_pct ?? 0;
    const hits = quest.hits ?? 0;
    const target = quest.target_days ?? 5;
    const today = new Date();
    const weekEnd = new Date(quest.week_end);
    const daysLeft = Math.max(0, Math.ceil((weekEnd - today) / 86400000) + 1);

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                data-testid="weekly-quest-card"
            >
                <Card className={`overflow-hidden border ${
                    isDone
                        ? 'border-emerald-400/40 bg-gradient-to-br from-emerald-500/10 via-card to-card'
                        : isExpired
                            ? 'border-muted bg-card opacity-70'
                            : 'border-amber-400/40 bg-gradient-to-br from-amber-500/5 via-card to-card'
                }`}>
                    <CardContent className="p-4 space-y-3">
                        <div className="flex items-start gap-3">
                            <div className="text-3xl shrink-0 leading-none mt-0.5" aria-hidden="true">
                                {isDone ? '🏆' : (quest.emoji || '🎯')}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5">
                                    <Trophy className="w-3 h-3 text-amber-500" />
                                    <p className="text-[10px] uppercase tracking-wide text-amber-600 dark:text-amber-400 font-semibold">
                                        {language === 'de' ? 'Wochen-Quest' : 'Weekly quest'}
                                    </p>
                                </div>
                                <h3 className="font-display font-bold text-sm leading-tight mt-0.5" data-testid="quest-title">
                                    {quest.title}
                                </h3>
                                <p className="text-xs text-muted-foreground leading-snug mt-1" data-testid="quest-body">
                                    {quest.body}
                                </p>
                            </div>
                            <div className="text-right shrink-0">
                                <div className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary">
                                    <Sparkles className="w-3 h-3" />
                                    +{quest.reward_xp} XP
                                </div>
                            </div>
                        </div>

                        <QuestProgress
                            hits={hits} target={target} pct={pct}
                            isDone={isDone} isExpired={isExpired}
                            daysLeft={daysLeft} language={language}
                            barClass={isDone ? 'bg-emerald-500' : 'bg-gradient-to-r from-amber-400 to-orange-500'}
                            testId="quest-progress-text"
                        />
                    </CardContent>
                </Card>
            </motion.div>
        </AnimatePresence>
    );
}


function LabQuestRow({ quest, language }) {
    const isDone = quest.status === 'completed';
    const isExpired = quest.status === 'expired';
    const pct = quest.progress_pct ?? 0;
    const hits = quest.hits ?? 0;
    const target = quest.target_days ?? 3;
    const today = new Date();
    const weekEnd = new Date(quest.week_end);
    const daysLeft = Math.max(0, Math.ceil((weekEnd - today) / 86400000) + 1);

    return (
        <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.15 }}
            data-testid={`lab-quest-${quest.lab_marker}`}
        >
            <Card className={`overflow-hidden border-2 ${
                isDone
                    ? 'border-emerald-400/50 bg-gradient-to-br from-emerald-500/10 via-card to-card'
                    : 'border-purple-400/40 bg-gradient-to-br from-purple-500/10 via-card to-card'
            }`}>
                <CardContent className="p-4 space-y-3">
                    <div className="flex items-start gap-3">
                        <div className="text-3xl shrink-0 leading-none mt-0.5" aria-hidden="true">
                            {isDone ? '🏆' : (quest.emoji || '💊')}
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                                <FlaskConical className="w-3 h-3 text-purple-600" />
                                <p className="text-[10px] uppercase tracking-wide text-purple-700 dark:text-purple-300 font-semibold">
                                    {language === 'de' ? 'Lab-Quest' : 'Lab quest'}
                                </p>
                                {quest.lab_marker && (
                                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 font-mono uppercase">
                                        {quest.lab_marker.replace('_', ' ')}
                                    </span>
                                )}
                            </div>
                            <h3 className="font-display font-bold text-sm leading-tight mt-0.5" data-testid={`lab-quest-${quest.lab_marker}-title`}>
                                {quest.title}
                            </h3>
                            <p className="text-xs text-muted-foreground leading-snug mt-1" data-testid={`lab-quest-${quest.lab_marker}-body`}>
                                {quest.body}
                            </p>
                        </div>
                        <div className="text-right shrink-0">
                            <div className="inline-flex items-center gap-1 text-[11px] font-semibold text-purple-700 dark:text-purple-300">
                                <Sparkles className="w-3 h-3" />
                                +{quest.reward_xp} XP
                            </div>
                        </div>
                    </div>

                    <QuestProgress
                        hits={hits} target={target} pct={pct}
                        isDone={isDone} isExpired={isExpired}
                        daysLeft={daysLeft} language={language}
                        barClass={isDone ? 'bg-emerald-500' : 'bg-gradient-to-r from-purple-400 to-fuchsia-500'}
                        testId={`lab-quest-${quest.lab_marker}-progress`}
                    />
                </CardContent>
            </Card>
        </motion.div>
    );
}


function QuestProgress({ hits, target, pct, isDone, isExpired, daysLeft, language, barClass, testId }) {
    return (
        <div className="space-y-1">
            <div className="flex items-center justify-between text-[11px]">
                <span className="font-medium" data-testid={testId}>
                    {hits} / {target} {language === 'de' ? 'Tage geschafft' : 'days hit'}
                </span>
                <span className="text-muted-foreground inline-flex items-center gap-1">
                    {isDone ? (
                        <><CheckCircle2 className="w-3 h-3 text-emerald-500" />
                        {language === 'de' ? 'Erledigt' : 'Done'}</>
                    ) : isExpired ? (
                        language === 'de' ? 'Abgelaufen' : 'Expired'
                    ) : (
                        <><Clock className="w-3 h-3" />
                        {daysLeft === 1
                            ? (language === 'de' ? 'Letzter Tag' : 'Last day')
                            : (language === 'de' ? `${daysLeft} Tage übrig` : `${daysLeft} days left`)}</>
                    )}
                </span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
                <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
                    className={`h-full rounded-full ${barClass}`}
                />
            </div>
        </div>
    );
}
